"use client";

import { useTranslations } from "next-intl";
import { type RefObject, useEffect, useState } from "react";
import { BounceLoader } from "@/components/common/bounce-loader";
import { ButtonLoadingIcon } from "@/components/common/buttons/button-loading-icon";
import { FloatingBottomBar } from "@/components/floating-bottom-bar";
import type { DietCatalogCateringType } from "@/features/diets/types";
import { getLocalePrice } from "@/lib/utilities";
import { cn } from "@/lib/utils";
import type { CartPricing } from "../../types";
import { DeliveryCost } from "../delivery-cost";

export const ConfigurationBar = ({
  days,
  disabled,
  edit,
  loading,
  pricingRef,
  pricing,
  addonsTotal = 0,
  costs,
}: {
  days?: number;
  disabled?: boolean;
  edit: boolean;
  loading?: boolean;
  pricingRef: RefObject<HTMLDivElement | null>;
  // Diet only — `pricing.final` never covers the position's addons.
  pricing?: CartPricing;
  // Charged addon value of the position, added to the total shown in the bar.
  addonsTotal?: number;
  costs?: DietCatalogCateringType["deliveryCosts"];
}) => {
  const t = useTranslations();
  const [showTotalPrice, setShowTotalPrice] = useState(false);

  useEffect(() => {
    const calendarElement = document.getElementById("calendar-date-picker");

    if (!calendarElement) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowTotalPrice(entry?.isIntersecting ?? false);
      },
      {
        threshold: 0,
      },
    );

    observer.observe(calendarElement);

    return () => {
      observer.disconnect();
    };
  }, []);

  const formatDays = (value: string | number): string => {
    const number = parseInt(value.toString(), 10);
    return isNaN(number) ? "" : t("dateTime.days", { count: number });
  };

  const LoadingIcon = () =>
    disabled ? (
      <ButtonLoadingIcon className="ml-2.5 inline-block" loading={disabled} />
    ) : null;

  const dailyPriceContent = (
    <div className="flex flex-col justify-center">
      <span
        className={cn(
          "font-bold text-lg leading-6.5",
          pricing?.discounts ? "text-brand-red" : "text-brand-violet",
        )}
      >
        {loading ? (
          <BounceLoader className="my-2 h-4" />
        ) : (
          pricing?.final.gross && (
            <span>
              <span className="font-bold">
                {getLocalePrice({
                  price: pricing.final.gross / (days || 1),
                })}
              </span>
              <span> {t("cart.configuration.perDay")}</span>
            </span>
          )
        )}
      </span>
      {costs && (
        <DeliveryCost
          {...costs}
          align="left"
          calculated={!!pricing?.delivery}
          hideNotFree
        />
      )}
    </div>
  );

  const totalPriceContent = (
    <div className="flex flex-col justify-center">
      {(!costs || pricing?.delivery) && (
        <span className="font-medium text-brand-green1-300">
          {t("cart.pricing.totalPrice")}:
        </span>
      )}
      <div className="flex items-center gap-2">
        <div className="rounded-sm border px-2 py-1 text-xs">
          <p>{formatDays(days || "")}</p>
        </div>
        <span className="font-bold text-brand-violet text-lg leading-6.5">
          {getLocalePrice({
            price: (pricing?.final.gross ?? 0) + addonsTotal,
          })}
        </span>
      </div>
      {costs && (
        <DeliveryCost
          {...costs}
          align="left"
          calculated={!!pricing?.delivery}
          hideNotFree
        />
      )}
    </div>
  );

  return (
    <FloatingBottomBar
      button={{
        disabled: disabled || loading,
        form: "configure-diet-form",
        label: edit ? (
          <>
            <span>{t("cart.configuration.save")}</span>
            <LoadingIcon />
          </>
        ) : (
          <>
            <span>{t("cart.configuration.addToCartSmall")}</span>
            <LoadingIcon />
          </>
        ),
        type: "submit",
        className: "px-6!",
      }}
      content={showTotalPrice ? totalPriceContent : dailyPriceContent}
      hidingElementRef={pricingRef}
    />
  );
};
